import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff } from "lucide-react";
import { authAPI } from "../api";
import GoogleSignInButton from "../components/GoogleSignInButton";
import AuthShell from "../components/AuthShell";
import Button from "../components/atoms/Button";

export default function Login({ onLogin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.from || "/dashboard";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password.trim()) { setError("Please fill in all fields."); return; }
    if (!/\S+@\S+\.\S+/.test(email))      { setError("Please enter a valid email address."); return; }
    if (password.length < 6)              { setError("Password must be at least 6 characters."); return; }
    setLoading(true);
    try {
      const data = await authAPI.login(email, password);
      localStorage.setItem("currentDay", String(data.user.currentDay || 1));
      localStorage.setItem("onboarded", "true");
      onLogin(data.user, data.token);
      navigate(returnTo);
    } catch (err) { setError(err.message || "Invalid email or password."); }
    finally { setLoading(false); }
  }

  return (
    <AuthShell
      quote={<>Pull up a chair. Your English is about to get a lot more <em>confident.</em></>}
      attribution="The shorthand"
    >
      <h1 className="font-display" style={{ fontSize: 48, lineHeight: 1.05, fontWeight: 400, letterSpacing: "-0.025em", margin: "0 0 8px" }}>
        Welcome <em style={{ color: "var(--gold-500)" }}>back.</em>
      </h1>
      <p style={{ color: "var(--fg-3)", fontSize: 15, margin: "0 0 28px" }}>
        Pick up where you left off.
      </p>

      {error && <ErrorBox>{error}</ErrorBox>}

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Field id="email" label="Email">
          <InputWithIcon icon={<Mail size={18} strokeWidth={1.5} />}>
            <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" style={inputStyle} />
          </InputWithIcon>
        </Field>

        <Field id="password" label="Password">
          <InputWithIcon icon={<Lock size={18} strokeWidth={1.5} />} trailing={
            <button type="button" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? "Hide password" : "Show password"}
              style={{ background: "transparent", border: 0, color: "var(--fg-3)", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}>
              {showPassword ? <EyeOff size={18} strokeWidth={1.5} /> : <Eye size={18} strokeWidth={1.5} />}
            </button>
          }>
            <input id="password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" style={inputStyle} />
          </InputWithIcon>
        </Field>

        <div style={{ textAlign: "right" }}>
          <Link to="/forgot-password" style={{ color: "var(--gold-700)", fontSize: 13, fontWeight: 600, textDecoration: "none", borderBottom: "1px solid currentColor" }}>
            I forgot my password
          </Link>
        </div>

        <Button type="submit" variant="primary" disabled={loading} style={{ width: "100%", justifyContent: "center" }}>
          {loading ? "Signing in…" : "Continue"}
        </Button>
      </form>

      <Divider>or</Divider>

      <GoogleSignInButton
        onSuccess={(user, token) => {
          localStorage.setItem("currentDay", String(user.currentDay || 1));
          localStorage.setItem("onboarded", "true");
          onLogin(user, token);
          navigate(returnTo);
        }}
        onError={(msg) => setError(msg)}
      />

      <p style={{ marginTop: 28, color: "var(--fg-3)", fontSize: 14, textAlign: "center" }}>
        New here?{" "}
        <Link to="/onboarding" style={{ color: "var(--gold-700)", fontWeight: 600, textDecoration: "none", borderBottom: "1px solid currentColor" }}>
          Start free →
        </Link>
      </p>
    </AuthShell>
  );
}

export function Field({ id, label, hint, children }) {
  return (
    <div>
      <label htmlFor={id} style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--fg-1)", marginBottom: 6 }}>{label}</label>
      {children}
      {hint && <p style={{ fontSize: 12, color: "var(--fg-3)", margin: "6px 0 0" }}>{hint}</p>}
    </div>
  );
}

export function InputWithIcon({ icon, trailing, children }) {
  return (
    <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
      {icon && (
        <span style={{ position: "absolute", left: 14, color: "var(--fg-3)", display: "flex", pointerEvents: "none" }}>{icon}</span>
      )}
      {children}
      {trailing && (
        <span style={{ position: "absolute", right: 14, display: "flex" }}>{trailing}</span>
      )}
    </div>
  );
}

export const inputStyle = {
  width: "100%",
  padding: "12px 14px 12px 44px",
  border: "1px solid var(--border-2)",
  borderRadius: "var(--radius-xs)",
  background: "#fff",
  color: "var(--fg-1)",
  fontFamily: "var(--font-body)",
  fontSize: 15,
  outline: "none",
  transition: "border-color 150ms cubic-bezier(.2,.8,.2,1), box-shadow 150ms cubic-bezier(.2,.8,.2,1)",
};

export function ErrorBox({ children }) {
  return (
    <div role="alert" style={{ background: "#fdf0f1", border: "1px solid #ddb1b6", color: "var(--berry-dark)", borderRadius: 8, padding: "10px 14px", fontSize: 14, marginBottom: 16 }}>
      {children}
    </div>
  );
}

export function Divider({ children }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "24px 0" }}>
      <span style={{ flex: 1, height: 1, background: "var(--border-1)" }} />
      <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.14em", color: "var(--fg-3)" }}>{children}</span>
      <span style={{ flex: 1, height: 1, background: "var(--border-1)" }} />
    </div>
  );
}
